// The touch log (ui-map-interface.md §8 "Touch log", R1497).
//
// A touch fault that shows on one device alone cannot be read from a desk.
// The iPhone fault of R1494 showed only in an event log of a real device,
// and two fixes that guessed at it were reverted. The touch log puts the
// same kind of log inside the client, so a player anywhere can record one
// gesture and paste the text into a report.
//
// The rules:
//
// - **It is opt-in.** `?touchlog=1` turns it on and stores the flag;
//   `?touchlog=0` turns it off and clears the flag. A page with no
//   parameter reads the stored flag, because the hub builds the address of
//   the game page and the parameter does not survive that navigation. The
//   flag expires `FLAG_TTL_MS` after `?touchlog=1`, so a player who forgets
//   `?touchlog=0` does not keep the chip (R1524).
// - **It never changes a gesture.** A drag released over the chip or its
//   menu still drops (`inTouchLog`), because the chip covers part of the
//   map and a log must not change what it records (R1524).
// - **Off costs nothing.** With the flag off, `startTouchLog` returns null
//   before it adds a listener or a DOM node, and `touchLogSink` answers
//   null, so a hook at a call site is one null test and builds no string.
// - **It never drops an edge.** A down, an up, a cancel and a capture line
//   always go into the ring. A move is thinned to one line per pointer per
//   `MOVE_GAP_MS`, plus the first move after a down and the last move
//   before the up.
// - **Its words are DOM.** The chip and its three buttons are an HTML
//   overlay, never canvas text (client/CLAUDE.md, the canvas/DOM split).
//   The log lines themselves are machine text for a developer, not
//   sentences, so they stay in the code (data-layer.md §5.2).
//
// The two figures below are local constants of a debug tool. No game rule,
// no render figure and no operator reads them, so no data category owns
// them (R1497), in the way `?perf=1` keeps its own settle time in
// `fast/main.ts`.

import { touchLog as S } from '../gen/strings';
import { PROTOCOL } from '../gen/balance';
import { storeOf } from '../ui/localstore';

/** The ring keeps this many lines, and drops the oldest line after that. */
export const RING_LINES = 4000;
/** The least time between two logged moves of one pointer, in ms. */
export const MOVE_GAP_MS = 50;
/** The localStorage key of the flag. Its value is the expiry time in epoch ms. */
export const TOUCH_LOG_KEY = 'aquaphobia.touchlog';
/** The flag holds for this long after `?touchlog=1`, in ms (R1524). */
export const FLAG_TTL_MS = 60 * 60 * 1000;

/** The event clock of the browser, in ms. */
type Clock = () => number;

/** What the recorder needs to know of a pointer event. */
export interface PointerLike {
  type: string;
  timeStamp: number;
  pointerId: number;
  pointerType: string;
  isPrimary: boolean;
  button: number;
  buttons: number;
  clientX: number;
  clientY: number;
  target: EventTarget | null;
  defaultPrevented: boolean;
}

/** What the recorder needs to know of a touch event. */
export interface TouchLike {
  type: string;
  timeStamp: number;
  touches: { length: number };
  changedTouches: ArrayLike<{ identifier: number; clientX: number; clientY: number }>;
  target: EventTarget | null;
}

/**
 * The ring buffer of log lines. It holds no DOM node and no listener, so a
 * test drives it with plain objects.
 */
export class TouchLog {
  private readonly ring: string[] = [];
  /** The index of the oldest line once the ring is full. */
  private head = 0;
  private t0: number | null = null;
  /** The time of the last logged move per pointer. */
  private readonly lastMove = new Map<number, number>();
  /** The last move that the thinning held back, per pointer. */
  private readonly heldMove = new Map<number, string>();
  private lastTouchMove = -Infinity;
  private heldTouchMove: string | null = null;
  /** The header lines. CLEAR keeps them. */
  header: string[] = [];
  /** Called after each new line, so the chip can update its count. */
  onLine: () => void = () => {};

  constructor(
    readonly cap = RING_LINES,
    readonly moveGapMs = MOVE_GAP_MS,
    private readonly clock: Clock = () => performance.now(),
  ) {}

  /** The number of lines in the ring, the header not included. */
  get count(): number { return this.ring.length; }

  /** One line at a time on the event clock. */
  add(t: number, text: string): void {
    if (this.t0 === null) this.t0 = t;
    const line = `${(t - this.t0).toFixed(1).padStart(9)} ${text}`;
    if (this.ring.length < this.cap) this.ring.push(line);
    else {
      this.ring[this.head] = line;
      this.head = (this.head + 1) % this.cap;
    }
    this.onLine();
  }

  /** One line from a hook, which has no event: the time is the clock's. */
  note(text: string): void { this.add(this.clock(), text); }

  /** One pointer event. A move is thinned; every other type is kept. */
  pointer(e: PointerLike): void {
    const id = e.pointerId;
    const text = pointerText(e);
    if (e.type === 'pointermove') {
      const last = this.lastMove.get(id);
      if (last !== undefined && e.timeStamp - last < this.moveGapMs) {
        this.heldMove.set(id, text);
        return;
      }
      this.lastMove.set(id, e.timeStamp);
      this.heldMove.delete(id);
      this.add(e.timeStamp, text);
      return;
    }
    // The last move before an edge is the one a reader wants most: it says
    // where the finger was when the browser ended the press.
    const held = this.heldMove.get(id);
    if (held !== undefined) {
      this.heldMove.delete(id);
      this.add(this.lastMove.get(id) ?? e.timeStamp, `${held} (last)`);
    }
    if (e.type === 'pointerdown' || e.type === 'pointerup' || e.type === 'pointercancel') {
      // The next move of this pointer is a first move again.
      this.lastMove.delete(id);
    }
    this.add(e.timeStamp, text);
  }

  /** One touch event. A touchmove is thinned like a pointer move. */
  touch(e: TouchLike): void {
    const text = touchText(e);
    if (e.type === 'touchmove') {
      if (e.timeStamp - this.lastTouchMove < this.moveGapMs) {
        this.heldTouchMove = text;
        return;
      }
      this.lastTouchMove = e.timeStamp;
      this.heldTouchMove = null;
      this.add(e.timeStamp, text);
      return;
    }
    if (this.heldTouchMove !== null) {
      this.add(this.lastTouchMove, `${this.heldTouchMove} (last)`);
      this.heldTouchMove = null;
    }
    this.lastTouchMove = -Infinity;
    this.add(e.timeStamp, text);
  }

  /** The lines, oldest first. */
  lines(): string[] {
    return this.ring.slice(this.head).concat(this.ring.slice(0, this.head));
  }

  /** The whole log as one text: the header, then the lines. */
  text(): string {
    return this.header.concat(this.lines()).join('\n');
  }

  /** Empties the ring and restarts the clock. The header stays. */
  clear(): void {
    this.ring.length = 0;
    this.head = 0;
    this.t0 = null;
    this.lastMove.clear();
    this.heldMove.clear();
    this.lastTouchMove = -Infinity;
    this.heldTouchMove = null;
    this.onLine();
  }
}

/** An element as `tag#id.class`, or `-` for no element. */
export function describeTarget(t: EventTarget | null): string {
  if (t === null) return '-';
  const el = t as Partial<Element>;
  if (typeof el.tagName !== 'string') {
    return t === (globalThis as { window?: unknown }).window ? 'window' : 'document';
  }
  let s = el.tagName.toLowerCase();
  if (el.id) s += `#${el.id}`;
  const cls = typeof el.className === 'string' ? el.className.trim() : '';
  if (cls !== '') s += `.${cls.split(/\s+/).slice(0, 2).join('.')}`;
  return s;
}

function pointerText(e: PointerLike): string {
  const dp = e.defaultPrevented ? ' dp' : '';
  return `${e.type} id=${e.pointerId} ${e.pointerType}${e.isPrimary ? ' primary' : ''}`
    + ` b=${e.button}/${e.buttons} @${Math.round(e.clientX)},${Math.round(e.clientY)}`
    + ` ${describeTarget(e.target)}${dp}`;
}

function touchText(e: TouchLike): string {
  const c = e.changedTouches.length > 0 ? e.changedTouches[0] : null;
  const first = c ? ` first=${c.identifier}@${Math.round(c.clientX)},${Math.round(c.clientY)}` : '';
  return `${e.type} n=${e.touches.length} changed=${e.changedTouches.length}${first}`
    + ` ${describeTarget(e.target)}`;
}

/** The part of the window the header reads. Every field may be absent. */
export interface DeviceEnv {
  navigator?: Partial<Navigator> & { userAgentData?: { brands?: { brand: string; version: string }[]; mobile?: boolean; platform?: string } };
  devicePixelRatio?: number;
  screen?: Partial<Screen>;
  innerWidth?: number;
  innerHeight?: number;
  visualViewport?: Partial<VisualViewport> | null;
  matchMedia?: (q: string) => { matches: boolean };
  PointerEvent?: unknown;
  location?: { pathname?: string; search?: string };
}

/**
 * The header of a log: what the device says of itself. It reads every API
 * through a guard, because an old vendor browser lacks some of them, and a
 * header that throws would lose the whole log.
 */
export function deviceHeader(w: DeviceEnv): string[] {
  const out = ['# aquaphobia touch log'];
  const put = (k: string, read: () => unknown) => {
    let v: unknown;
    try { v = read(); } catch (err) { v = `error ${String(err)}`; }
    out.push(`# ${k}: ${v === undefined || v === null ? '-' : String(v)}`);
  };
  const nav = w.navigator;
  put('protocol', () => PROTOCOL);
  put('page', () => (w.location ? `${w.location.pathname ?? ''}${w.location.search ?? ''}` : undefined));
  put('ua', () => nav?.userAgent);
  put('brands', () => nav?.userAgentData?.brands
    ?.map((b) => `${b.brand} ${b.version}`).join(', '));
  put('ua mobile/platform', () => (nav?.userAgentData
    ? `${nav.userAgentData.mobile} ${nav.userAgentData.platform}` : undefined));
  put('dpr', () => w.devicePixelRatio);
  put('screen', () => (w.screen ? `${w.screen.width}x${w.screen.height}` : undefined));
  put('inner', () => (w.innerWidth !== undefined ? `${w.innerWidth}x${w.innerHeight}` : undefined));
  put('visualViewport', () => {
    const v = w.visualViewport;
    if (!v) return undefined;
    return `scale=${v.scale} offset=${v.offsetLeft},${v.offsetTop} size=${v.width}x${v.height}`;
  });
  put('maxTouchPoints', () => nav?.maxTouchPoints);
  put('pointer:coarse', () => (typeof w.matchMedia === 'function'
    ? w.matchMedia('(pointer: coarse)').matches : undefined));
  put('hover:none', () => (typeof w.matchMedia === 'function'
    ? w.matchMedia('(hover: none)').matches : undefined));
  put('PointerEvent', () => typeof w.PointerEvent === 'function');
  return out;
}

// ---------------------------------------------------------------------------
// The switch
// ---------------------------------------------------------------------------

/** A storage with the two calls the switch makes. Every call may throw. */
export interface FlagStore {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

/**
 * Reads the switch: `?touchlog=1` turns the log on and stores the flag
 * with its expiry time, `?touchlog=0` turns it off and clears the flag,
 * and no parameter reads the stored flag. An expired flag, or a value that
 * is no time (the `1` of the first build), reads as off and is cleared
 * (R1524). A storage that throws (a private window, blocked site data)
 * leaves the parameter alone in charge.
 */
export function readTouchLogSwitch(
  search: string, store: FlagStore | null, now: number = Date.now(),
): boolean {
  const p = new URLSearchParams(search).get('touchlog');
  if (p === '1') {
    try { store?.setItem(TOUCH_LOG_KEY, String(now + FLAG_TTL_MS)); } catch { /* the parameter still holds */ }
    return true;
  }
  if (p === '0') {
    try { store?.removeItem(TOUCH_LOG_KEY); } catch { /* the parameter still holds */ }
    return false;
  }
  try {
    const v = store?.getItem(TOUCH_LOG_KEY) ?? null;
    if (v === null) return false;
    if (Number(v) > now) return true;
    store?.removeItem(TOUCH_LOG_KEY);
    return false;
  } catch { return false; }
}

/**
 * Is an element part of the touch log's chip or menu? A drag release
 * there is no release over the chrome (R1524).
 */
export function inTouchLog(el: Element | null): boolean {
  return el !== null && typeof el.closest === 'function' && el.closest('#touchlog') !== null;
}


// ---------------------------------------------------------------------------
// The page half
// ---------------------------------------------------------------------------

/** The log of this page, or null while the flag is off. */
let active: TouchLog | null = null;
/** The one bound hook, built once, so a call site allocates nothing. */
let sink: ((line: string) => void) | null = null;

/**
 * The hook a gesture writes to, or null while the log is off. A call site
 * reads it once when it installs and tests it for null at each event, so
 * with the log off it builds no string.
 */
export function touchLogSink(): ((line: string) => void) | null { return sink; }

/**
 * Starts the log of this page when the switch says so, and answers it; with
 * the switch off it answers null and does nothing else. It is idempotent.
 */
export function startTouchLog(): TouchLog | null {
  if (active) return active;
  if (typeof window === 'undefined') return null;
  if (!readTouchLogSwitch(location.search, storeOf())) return null;
  const log = new TouchLog();
  log.header = deviceHeader(window as unknown as DeviceEnv);
  active = log;
  sink = (line) => log.note(line);
  listen(log);
  mountChip(log);
  return log;
}

/** Forgets the page's log. Tests call it; the page never does. */
export function stopTouchLogForTest(): void {
  active = null;
  sink = null;
}

/** The raw events, in the capture phase on the window. */
function listen(log: TouchLog): void {
  const opts = { capture: true, passive: true };
  const onPointer = (e: Event) => log.pointer(e as PointerEvent);
  for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel',
    'gotpointercapture', 'lostpointercapture']) {
    window.addEventListener(type, onPointer, opts);
  }
  // `pointerleave` fires on every element the pointer leaves; the canvas
  // is the one a reader needs.
  window.addEventListener('pointerleave', (e) => {
    if ((e.target as Partial<Element> | null)?.tagName === 'CANVAS') log.pointer(e);
  }, opts);
  const onTouch = (e: Event) => log.touch(e as TouchEvent);
  for (const type of ['touchstart', 'touchmove', 'touchend', 'touchcancel']) {
    window.addEventListener(type, onTouch, opts);
  }
  window.addEventListener('contextmenu', (e) => {
    log.add(e.timeStamp, `contextmenu @${Math.round(e.clientX)},${Math.round(e.clientY)} ${describeTarget(e.target)}`);
    // The handlers decide after this line. A task later the event is done,
    // and `defaultPrevented` says whether the browser menu was stopped.
    setTimeout(() => log.note(`contextmenu prevented=${e.defaultPrevented}`), 0);
  }, { capture: true });
  document.addEventListener('visibilitychange', (e) => {
    log.add(e.timeStamp, `visibilitychange hidden=${document.hidden}`);
  }, { capture: true });
  window.addEventListener('blur', (e) => {
    if (e.target === window) log.add(e.timeStamp, 'blur');
  }, { capture: true });
}

/** The chip in the corner, and its three buttons. */
function mountChip(log: TouchLog): void {
  const mount = () => {
    const root = document.createElement('div');
    root.id = 'touchlog';
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'chip';
    const menu = document.createElement('div');
    menu.className = 'menu';
    const button = (label: string, act: () => void) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      // A click, not a pointerdown: the clipboard and the share sheet need
      // the user activation a click carries, and a touch pointerdown does
      // not carry it. The chip stays in place between press and release,
      // so the click is not lost (§16.8).
      b.addEventListener('click', act);
      menu.appendChild(b);
    };
    const status = document.createElement('div');
    status.className = 'status';
    const area = document.createElement('textarea');
    area.readOnly = true;
    const say = (s: string) => { status.textContent = s; };

    button(S.copy, () => {
      const text = log.text();
      const fallback = () => {
        // A vendor browser over plain HTTP often has no clipboard. The text
        // is then put in a selected field, so the player can copy it by hand.
        area.value = text;
        root.classList.add('manual');
        area.focus();
        area.select();
        let ok = false;
        try { ok = document.execCommand('copy'); } catch { ok = false; }
        say(ok ? S.copied : S.select_and_copy);
      };
      const clip = navigator.clipboard;
      if (!clip || typeof clip.writeText !== 'function') { fallback(); return; }
      clip.writeText(text).then(() => say(S.copied), fallback);
    });
    if (typeof navigator.share === 'function') {
      button(S.share, () => {
        navigator.share({ text: log.text() }).catch(() => { /* the player closed the sheet */ });
      });
    }
    button(S.clear, () => {
      log.clear();
      root.classList.remove('manual');
      area.value = '';
      say('');
    });
    menu.append(status, area);
    chip.addEventListener('click', () => root.classList.toggle('open'));
    root.append(chip, menu);
    document.body.appendChild(root);

    // The count updates at most twice a second, so a burst of moves does
    // not write the DOM once per event.
    let queued = false;
    const paint = () => {
      queued = false;
      chip.textContent = S.chip({ count: log.count });
    };
    log.onLine = () => {
      if (queued) return;
      queued = true;
      setTimeout(paint, 500);
    };
    paint();
  };
  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount, { once: true });
}
